/**
 * timelineUtils — Timeline 시각화용 시간/ETA 헬퍼.
 *
 * 시간 축 범위: 07:00 ~ 17:00 (= 420 ~ 1020분, 총 600분).
 *
 * ETA(예상 마감 시각) 계산 규칙:
 *   1. 출하 완료 (remain <= 0):  lastcar 시각 또는 현재 시각
 *   2. 진행 중 (shipped > 0):    현재 출하 속도(shipped/elapsed)로 잔량 외삽
 *   3. 대기 (shipped == 0):      기본 속도 1㎥/분(=60㎥/시간) 가정
 */
import type { RqcShipment } from "@/domain/rqc";

export const TIMELINE_START_MIN = 7 * 60;   // 07:00 = 420
export const TIMELINE_END_MIN = 17 * 60;    // 17:00 = 1020
export const TIMELINE_SPAN_MIN = TIMELINE_END_MIN - TIMELINE_START_MIN; // 600

/** "0855" / "08:55" → 8*60+55 = 535. 파싱 실패 시 null */
export function hhmmToMin(s: string): number | null {
  if (!s) return null;
  const d = String(s).replace(/[^\d]/g, "");
  if (d.length !== 4) return null;
  const h = parseInt(d.slice(0, 2), 10);
  const m = parseInt(d.slice(2), 10);
  if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

/** "8952번 차량 (0855)" → 8*60+55. 괄호 안의 4자리 추출 */
export function extractLastcarTime(s: string): number | null {
  if (!s) return null;
  const m = s.match(/\((\d{4})\)/);
  return m ? hhmmToMin(m[1]) : null;
}

/**
 * 첫차 필드 파싱 — 두 가지 형태 지원:
 *   - 실제 첫차 시각: "0855", "08:55"
 *   - 도착 예정(착): "09착", "0900착", "10착", "09:00 착"
 *
 * 분(min) + isEta 플래그 반환. Timeline에서 점 모양을 다르게 그리는 데 사용.
 */
export interface FirstcarParsed {
  min: number;
  /** true = 도착 예정(ETA), false = 실제 첫차 시각 */
  isEta: boolean;
}
export function parseFirstcar(s: string): FirstcarParsed | null {
  if (!s) return null;
  const trimmed = String(s).trim();
  const isEta = /착/.test(trimmed);
  const min = parseTimeFlexible(trimmed);
  return min == null ? null : { min, isEta };
}

/** HH:MM / HHMM / HH 형식을 모두 인식. 시 범위 0~23, 분 범위 0~59. */
function parseTimeFlexible(s: string): number | null {
  // HH:MM
  let m = s.match(/(\d{1,2}):(\d{1,2})/);
  if (m) {
    const h = parseInt(m[1], 10), mm = parseInt(m[2], 10);
    if (h >= 0 && h <= 23 && mm >= 0 && mm <= 59) return h * 60 + mm;
  }
  // HHMM (4자리)
  m = s.match(/(\d{4})/);
  if (m) {
    const h = parseInt(m[1].slice(0, 2), 10);
    const mm = parseInt(m[1].slice(2), 10);
    if (h >= 0 && h <= 23 && mm >= 0 && mm <= 59) return h * 60 + mm;
  }
  // HH (1~2자리, "09착" 같이 단독)
  m = s.match(/^\s*(\d{1,2})/);
  if (m) {
    const h = parseInt(m[1], 10);
    if (h >= 0 && h <= 23) return h * 60;
  }
  return null;
}

/** 분 → HH:MM 표시 */
export function minToHHMM(min: number): string {
  if (min < 0 || !isFinite(min)) return "—";
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** KST 현재 분(0~1439) */
export function nowKstMin(): number {
  const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/** 시간 분 → Timeline 영역 내 % */
export function timeToPct(min: number): number {
  const pct = ((min - TIMELINE_START_MIN) / TIMELINE_SPAN_MIN) * 100;
  return Math.max(0, Math.min(100, pct));
}

/** 한 사이트의 막대 범위 + 실시간 채움 비율 */
export interface ShipmentBarRange {
  startMin: number;
  endMin: number;
  /** 채움(fill) 비율 0~100. 실시간(현재 시각) 기준 또는 완료 시 100 */
  fillPct: number;
  /** end가 추정값인지 (true) 실측값인지 (false) */
  isEndEstimated: boolean;
}

/** details에서 가져오는 실제 출하 시각 메타 (optional input) */
export interface BarMetaInput {
  firstMin: number;
  lastMin: number;
  count: number;
  avgIntervalMin: number | null;
}

const DEFAULT_RATE = 1.0; // m³/분 = 60㎥/시간 (대기 상태 기본 가정)

/**
 * 막대 범위 계산. 사용자 정의 규칙:
 *
 *  - 대기(waiting): firstcar(예정) → 예정 시작 + 기본속도(qty/1m³분). fill=0%.
 *  - 진행 중(in_progress):
 *      · 실제 시각 meta 있으면 firstMin부터 시작 (없으면 firstcar HHMM 폴백)
 *      · 평균 간격 잡힌 경우(count>=2): lastMin + 남은 탕수 × 평균간격
 *      · 평균 간격 미정(count<2): 현재 시각 기준 elapsed × (qty/shipped)로 외삽
 *      · fill = 실시간 진척 (현재 시각 / 막대 전체 폭)
 *  - 완료(done): firstMin → lastMin (실측 둘 다 있으면). fill=100%.
 *
 * details meta가 전혀 없으면 firstcar(HHMM) 폴백. 그래도 없으면 null 반환 (= 막대 그리지 않음).
 */
export function computeBarRange(
  s: RqcShipment,
  nowMin: number,
  meta: BarMetaInput | undefined,
): ShipmentBarRange | null {
  const isDone = s.shipped > 0 && s.remain <= 0.5;
  const isInProgress = s.shipped > 0 && !isDone;
  const isWaiting = s.shipped <= 0;

  const firstcarHHMM = hhmmToMin(s.firstcar); // ETA "착"이면 null

  // ── 대기: firstcar(예정) 위치에서 빈 막대만 표기
  if (isWaiting) {
    if (firstcarHHMM == null) return null; // 시작점 모르면 막대 안 그림
    const endMin = Math.min(TIMELINE_END_MIN, firstcarHHMM + Math.ceil(s.qty / DEFAULT_RATE));
    return { startMin: firstcarHHMM, endMin, fillPct: 0, isEndEstimated: true };
  }

  // ── 시작점: 실제 첫차 시각(meta) > firstcar HHMM 폴백
  const startMin = meta?.firstMin ?? firstcarHHMM;
  if (startMin == null) return null;

  // ── 완료
  if (isDone) {
    const endMin = meta?.lastMin ?? extractLastcarTime(s.lastcar) ?? Math.max(startMin, nowMin);
    return {
      startMin,
      endMin,
      fillPct: 100,
      isEndEstimated: false,
    };
  }

  // ── 진행 중
  let endMin: number;
  if (isInProgress && meta && meta.avgIntervalMin && meta.avgIntervalMin > 0 && meta.count >= 2) {
    // 평균 간격 사용: 잔량 외삽 — 남은 탕수 추정 = remain / (shipped/count)
    const perTrip = s.shipped / meta.count;
    const remainingTrips = perTrip > 0 ? s.remain / perTrip : 0;
    endMin = Math.min(TIMELINE_END_MIN, meta.lastMin + remainingTrips * meta.avgIntervalMin);
  } else if (nowMin > startMin && s.shipped > 0) {
    // 폴백: 현재까지 elapsed로 외삽
    const elapsed = nowMin - startMin;
    const rate = Math.max(0.5, s.shipped / elapsed);
    const remainTime = Math.ceil(s.remain / rate);
    endMin = Math.min(TIMELINE_END_MIN, nowMin + remainTime);
  } else {
    // 초기 — 기본 속도 가정
    endMin = Math.min(TIMELINE_END_MIN, startMin + Math.ceil(s.qty / DEFAULT_RATE));
  }

  // 채움: 실시간 (현재 - 시작) / (끝 - 시작)
  const span = endMin - startMin;
  const elapsed = Math.max(0, nowMin - startMin);
  const fillPct = span > 0 ? Math.max(0, Math.min(100, (elapsed / span) * 100)) : 0;

  return { startMin, endMin, fillPct, isEndEstimated: true };
}
